import {
  WebsiteSourceKind,
  type IWebsiteSnapshot,
  type IWebsiteSnapshotPage,
  type IWebsiteSource,
  WEBSITE_SNAPSHOT_MAX_BYTES,
  WEBSITE_SNAPSHOT_MAX_PAGE_CHARS,
  WEBSITE_SNAPSHOT_MAX_PAGES,
  WEBSITE_SNAPSHOT_STALE_AFTER_DAYS,
} from '@principle-theorem/principle-core/interfaces';
import { type Timestamp, toMoment } from '@principle-theorem/shared';
import { duration } from 'moment-timezone';
import { md } from 'node-forge';

/** Why a website address typed by a practice cannot be read as a Source. */
export enum WebsiteUrlRejectionReason {
  Invalid = 'invalid',
  UnsupportedHost = 'unsupported-host',
  TooLong = 'too-long',
}

/** A website address accepted as a Source, in its normalised form. */
export interface IAcceptedWebsiteUrl {
  ok: true;
  url: string;
  source: IWebsiteSource;
}

/** A website address that cannot be read as a Source, and why. */
export interface IRejectedWebsiteUrl {
  ok: false;
  reason: WebsiteUrlRejectionReason;
}

export type WebsiteUrlNormalisation = IAcceptedWebsiteUrl | IRejectedWebsiteUrl;

/**
 * Topics the crawler is asked to prioritise when choosing subpages, and the
 * topics that keep a page in a Website Snapshot when it must be trimmed.
 */
export const WEBSITE_IMPORT_SUBPAGE_TARGETS = [
  'about',
  'team',
  'our dentists',
  'services',
  'treatments',
  'fees',
  'pricing',
  'payment',
  'contact',
  'parking',
  'location',
  'policy',
  'cancellation',
  'emergency',
  'new patients',
  'faq',
  'financial',
  'first visit',
  'patient information',
];

/** Subpages read alongside the root page in one website read. */
export const WEBSITE_IMPORT_SUBPAGE_COUNT = 15;

const maxInputLength = 2048;
const snapshotDocIdHashLength = 16;
const webProtocols = ['http:', 'https:'];
const schemePattern = /^[a-z][a-z\d+.-]*:\/\//i;
// The URL parser rewrites every IPv4 form (e.g. 2130706433) as dotted
// decimal.
const ipv4HostnamePattern = /^(\d+\.){3}\d+$/;
// The URL parser has already punycoded an internationalised host to ASCII,
// so anything else left in it (a space, "%20", "_") is not a hostname.
const hostnameCharactersPattern = /^[a-z\d.-]+$/i;
// Never end on the first half of a surrogate pair: an unpaired surrogate is
// not valid UTF-8 and would corrupt the stored text.
const trailingHighSurrogatePattern = /[\uD800-\uDBFF]$/;

/**
 * Owns the practice's website as a Source for Website Imports. Every step
 * that reads the website or its Website Snapshot applies these rules, so the
 * callable, the extractor and the editor agree on which address was read,
 * which document holds its snapshot, and which pages that snapshot keeps.
 */
export class WebsiteSource {
  /**
   * Turns the address a practice typed into the single form used to read its
   * website and to key its Website Snapshot, so "Example.com/" and
   * "https://example.com" share one snapshot. Addresses that are not public
   * websites are rejected: credentials would be handed to the crawler, and IP
   * addresses, localhost and dotless hosts point at private networks.
   */
  static normaliseUrl(input: string): WebsiteUrlNormalisation {
    const trimmed = input.trim();
    if (trimmed.length > maxInputLength) {
      return { ok: false, reason: WebsiteUrlRejectionReason.TooLong };
    }

    const parsed = WebsiteSource._parseUrl(
      schemePattern.test(trimmed) ? trimmed : `https://${trimmed}`
    );
    if (!parsed || !WebsiteSource._isWebAddressWithoutCredentials(parsed)) {
      return { ok: false, reason: WebsiteUrlRejectionReason.Invalid };
    }
    if (!WebsiteSource._isPublicHostname(parsed.hostname)) {
      return { ok: false, reason: WebsiteUrlRejectionReason.UnsupportedHost };
    }
    if (!hostnameCharactersPattern.test(parsed.hostname)) {
      // Browsers keep a space in the host as "%20" where Node refuses to parse
      // it at all; rejecting it here keeps the editor and the callable agreed.
      return { ok: false, reason: WebsiteUrlRejectionReason.Invalid };
    }

    // The URL parser has already lower-cased the host and dropped any default
    // port. Every trailing slash goes, so normalising again changes nothing.
    const path = parsed.pathname.replace(/\/+$/, '');
    const url = `${parsed.protocol}//${parsed.host}${path}${parsed.search}`;
    return { ok: true, url, source: { kind: WebsiteSourceKind.Website, url } };
  }

  /**
   * Keys the latest Website Snapshot of a Source, so the importer and the
   * editor read and write the same document. Hashing keeps URL characters
   * such as "/" out of the Firestore document id. Expects a Source produced by
   * `WebsiteSource.normaliseUrl`, which is what makes equivalent addresses
   * share an id.
   */
  static snapshotDocId(source: IWebsiteSource): string {
    const digest = md.sha256
      .create()
      .update(`${source.kind}:${source.url}`, 'utf8')
      .digest()
      .toHex();
    return `website_${digest.slice(0, snapshotDocIdHashLength)}`;
  }

  /**
   * Decides whether a crawled page belongs to the practice's own website, so
   * pages on other hosts are never stored or cited as Provenance. A leading
   * "www." is ignored because practices use both forms of their address.
   */
  static isSameSite(pageUrl: string, source: IWebsiteSource): boolean {
    const pageHostname = WebsiteSource._siteHostname(pageUrl);
    return (
      !!pageHostname && pageHostname === WebsiteSource._siteHostname(source.url)
    );
  }

  /**
   * Decides whether an import may reuse a stored Website Snapshot instead of
   * reading the website again. A snapshot of a different address, or one at
   * least `WEBSITE_SNAPSHOT_STALE_AFTER_DAYS` old, is never reused.
   */
  static isSnapshotUsable(
    snapshot: IWebsiteSnapshot | undefined,
    source: IWebsiteSource,
    now: Timestamp
  ): boolean {
    if (!snapshot || snapshot.source.url !== source.url) {
      return false;
    }
    const ageInMilliseconds = toMoment(now).diff(toMoment(snapshot.fetchedAt));
    return (
      ageInMilliseconds <
      duration(WEBSITE_SNAPSHOT_STALE_AFTER_DAYS, 'days').asMilliseconds()
    );
  }

  /**
   * Fits crawled pages into one Website Snapshot document, well under
   * Firestore's 1 MiB document limit. When pages must go, those matching no
   * target topic go first, as they are the least likely to answer a Knowledge
   * Base field. The root page is always kept because it anchors the site.
   */
  static trimSnapshotToBudget(
    pages: IWebsiteSnapshotPage[],
    targets: readonly string[]
  ): IWebsiteSnapshotPage[] {
    const cappedPages = WebsiteSource._capPageCount(
      pages.map((page) => WebsiteSource._clampPageText(page))
    );
    const onTopicPages = WebsiteSource._dropPagesUntilWithinBudget(
      cappedPages,
      (page) => !page.isRoot && !WebsiteSource.isTargetPage(page, targets)
    );
    return WebsiteSource._dropPagesUntilWithinBudget(
      onTopicPages,
      (page) => !page.isRoot
    );
  }

  /**
   * Decides whether a page covers one of the `targets` topics, which makes it
   * likely to answer a Knowledge Base field: such pages are kept when a
   * snapshot is trimmed and read first when proposals are extracted. A target
   * term must start a word of the page's URL path, query or title, so
   * "/our-dentists" matches "our dentists" but "whereabouts" does not match
   * "about". The host is ignored because every page shares it, and the root
   * page gets no special treatment; callers that always keep it check
   * `isRoot` themselves.
   */
  static isTargetPage(
    page: IWebsiteSnapshotPage,
    targets: readonly string[]
  ): boolean {
    const pageWords = ` ${WebsiteSource._normaliseWords(
      `${WebsiteSource._urlPathAndQuery(page.url)} ${page.title ?? ''}`
    )}`;
    return targets.some((term) =>
      pageWords.includes(` ${WebsiteSource._normaliseWords(term)}`)
    );
  }

  /**
   * Decides whether a pathname contains any of the given terms as whole words,
   * so "/sign-in" matches "sign-in" but "/smile-designing" does not match
   * "signin" and "/dr-carter" does not match "cart". Percent-escapes and
   * punctuation are treated as word breaks.
   */
  static pathHasWord(pathname: string, terms: readonly string[]): boolean {
    const pathWords = ` ${WebsiteSource._normaliseWords(pathname)} `;
    return terms.some((term) =>
      pathWords.includes(` ${WebsiteSource._normaliseWords(term)} `)
    );
  }

  private static _parseUrl(value: string): URL | undefined {
    try {
      return new URL(value);
    } catch {
      return undefined;
    }
  }

  private static _isWebAddressWithoutCredentials(url: URL): boolean {
    return (
      webProtocols.includes(url.protocol) && !url.username && !url.password
    );
  }

  private static _isPublicHostname(hostname: string): boolean {
    if (hostname.startsWith('[') || ipv4HostnamePattern.test(hostname)) {
      return false;
    }
    const labels = hostname.replace(/\.$/, '').split('.');
    return (
      labels.length > 1 &&
      labels.every((label) => label.length > 0) &&
      labels[labels.length - 1] !== 'localhost'
    );
  }

  private static _siteHostname(url: string): string | undefined {
    return WebsiteSource._parseUrl(url)?.hostname.replace(/^www\./, '');
  }

  private static _clampPageText(
    page: IWebsiteSnapshotPage
  ): IWebsiteSnapshotPage {
    if (page.text.length <= WEBSITE_SNAPSHOT_MAX_PAGE_CHARS) {
      return page;
    }
    const text = page.text
      .slice(0, WEBSITE_SNAPSHOT_MAX_PAGE_CHARS)
      .replace(trailingHighSurrogatePattern, '');
    return { ...page, text };
  }

  private static _capPageCount(
    pages: IWebsiteSnapshotPage[]
  ): IWebsiteSnapshotPage[] {
    const firstPages = pages.slice(0, WEBSITE_SNAPSHOT_MAX_PAGES);
    const rootPage = pages.find((page) => page.isRoot);
    if (!rootPage || firstPages.includes(rootPage)) {
      return firstPages;
    }
    return [...firstPages.slice(0, WEBSITE_SNAPSHOT_MAX_PAGES - 1), rootPage];
  }

  private static _dropPagesUntilWithinBudget(
    pages: IWebsiteSnapshotPage[],
    isDroppable: (page: IWebsiteSnapshotPage) => boolean
  ): IWebsiteSnapshotPage[] {
    if (WebsiteSource._encodedSize(pages) <= WEBSITE_SNAPSHOT_MAX_BYTES) {
      return pages;
    }
    const dropIndex = pages.reduce(
      (lastIndex, page, index) => (isDroppable(page) ? index : lastIndex),
      -1
    );
    if (dropIndex < 0) {
      return pages;
    }
    return WebsiteSource._dropPagesUntilWithinBudget(
      [...pages.slice(0, dropIndex), ...pages.slice(dropIndex + 1)],
      isDroppable
    );
  }

  private static _encodedSize(pages: IWebsiteSnapshotPage[]): number {
    return new TextEncoder().encode(JSON.stringify(pages)).length;
  }

  private static _urlPathAndQuery(url: string): string {
    const parsed = WebsiteSource._parseUrl(url);
    return parsed ? `${parsed.pathname}${parsed.search}` : url;
  }

  /**
   * Lower-cases and turns punctuation and percent-escapes into single spaces,
   * then trims. Used by `isTargetPage` and `pathHasWord` to normalise text
   * for word matching.
   */
  private static _normaliseWords(value: string): string {
    return value
      .toLowerCase()
      .replace(/(%[\da-f]{2}|[^a-z\d])+/g, ' ')
      .trim();
  }
}
